import { useState, JSX } from 'react'

import { Square2StackIcon } from '@heroicons/react/20/solid'
import { AlertTriangleIcon } from 'lucide-react'
import { useAccount, useDisconnect } from 'wagmi'

import {
  LabelValue,
  List,
  NetworkDisplay,
} from '~/components/component-library'
import { AddressDisplay } from '~/components/component-library/AddressDisplay'
import {
  Dialog,
  DialogTrigger,
  SplitsDialogContent,
} from '~/components/component-library/Dialog'
import { CustomLogoutIcon } from '~/components/custom-icons/CustomLogoutIcon'
import { isListedChainId } from '~/utils'
import { shortenAddress } from '~/utils/addresses'
import { copyToClipboard } from '~/utils/copyToClipboard'

export const ConnectedWallet = ({ label }: { label: string }): JSX.Element => {
  const { address, chainId } = useAccount()
  const { disconnectAsync } = useDisconnect()

  const isSupported = isListedChainId(chainId)

  const [dialogOpen, setDialogOpen] = useState(false)

  if (!address) throw new Error('Connected address required for this component')

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(open) => {
        setDialogOpen(open)
      }}
    >
      <DialogTrigger asChild>
        <LabelValue
          label="Connected EOA signer"
          listIndex={0}
          value={
            <AddressDisplay
              address={address}
              layout="standard"
              hidePopover
              clickable={false}
            />
          }
        />
      </DialogTrigger>
      <SplitsDialogContent
        id="accountMenu"
        variant="info"
        clampTitle={false}
        title={
          <AddressDisplay address={address} layout="standard" hidePopover />
        }
      >
        <List
          items={[
            {
              key: 'connected-network',
              content: 'Connected network',
              meta: isSupported ? (
                <NetworkDisplay layout="standard" chainId={chainId} />
              ) : (
                <AlertTriangleIcon size={20} className={'text-yellow-500'} />
              ),
              actionIcon: <></>,
            },
            {
              key: 'copy-address',
              content: 'Copy address',
              onClick: () => copyToClipboard(address, 'Address copied'),
              meta: shortenAddress(address),
              actionIcon: <Square2StackIcon className="w-4" />,
            },
            {
              key: 'disconnect',
              content: 'Disconnect',
              onClick: () => disconnectAsync(),
              actionIcon: <CustomLogoutIcon className="w-4" />,
            },
          ]}
        />
      </SplitsDialogContent>
    </Dialog>
  )
}
